import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Lightbulb, 
  Calendar, 
  Building2, 
  Award, 
  Wrench, 
  ShieldCheck, 
  Cpu, 
  ExternalLink,
  ZoomIn,
  Car
} from 'lucide-react';
import PatentImg from "../../Media/patents.jpg"

const patentData = {
  title: "Suspension Link for Scorpio Refresh SUV",
  assignee: "Mahindra & Mahindra (Automotive) Ltd.",
  inventor: "Prof. Sanjay Dhir",
  applicationDate: "April 2007 (04/2007)",
  category: "Automotive Suspension & Mechanical Dynamics",
  domain: "Automotive R&D & Structural Flexibility",
  status: "Granted / Published Industrial Design",
  imageUrl: PatentImg,
  overview: "Innovative structural suspension link designed and developed during tenure at the Research & Development Department of Mahindra and Mahindra Automotive Ltd. Engineered to optimize chassis load distribution, reduce unsprung mass, and elevate ride handling dynamics for the Scorpio Refresh SUV platform.",
  highlights: [
    "Load-bearing kinematics optimization for rugged all-terrain capability",
    "Weight reduction through modular geometric cross-sectional design",
    "Vibration dampening and enhanced torsional rigidity for high-speed stability",
    "Seamless industrial integration into the assembly line of the Scorpio Refresh vehicle"
  ]
};

export default function Patents() {
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  return (
    <div className="container py-5 min-vh-100 position-relative text-light" style={{ zIndex: 1 }}>
      
      {/* 1. Header Banner */}
      <div className="row align-items-center g-4 mb-5">
        <div className="col-12 col-lg-8" data-aos="fade-right">
          <div className="d-inline-flex align-items-center gap-2 px-3 py-1 rounded-pill mb-3 sub-badge">
            <Lightbulb size={16} className="text-cyan" />
            <span className="small text-light fw-medium">Intellectual Property & Industrial Innovation</span>
          </div>

          <h1 className="display-5 fw-bold text-white mb-3">
            Patents & <span className="text-gradient">Innovations</span>
          </h1>

          <p className="lead text-secondary fs-6 mb-4 pe-lg-3">
            Patented industrial design and mechanical engineering breakthroughs developed in the 
            <strong> Research & Development Department of Mahindra & Mahindra Ltd.</strong> prior to academic appointments at DMS, IIT Delhi[cite: 2].
          </p>

          <div className="d-flex flex-wrap gap-3">
            <div className="p-3 rounded-3 glass-card-sm text-center" style={{ minWidth: '110px' }}>
              <span className="fs-4 fw-bold text-cyan">Mahindra</span>
              <span className="small text-secondary d-block" style={{ fontSize: '0.75rem' }}>Automotive R&D</span>
            </div>
            <div className="p-3 rounded-3 glass-card-sm text-center" style={{ minWidth: '110px' }}>
              <span className="fs-4 fw-bold text-warning">Scorpio SUV</span>
              <span className="small text-secondary d-block" style={{ fontSize: '0.75rem' }}>Platform Application</span>
            </div>
            <div className="p-3 rounded-3 glass-card-sm text-center" style={{ minWidth: '110px' }}>
              <span className="fs-4 fw-bold text-info">2007</span>
              <span className="small text-secondary d-block" style={{ fontSize: '0.75rem' }}>Filing Year</span>
            </div>
          </div>
        </div>

        {/* Industrial Engineering Card */}
        <div className="col-12 col-lg-4" data-aos="fade-left">
          <div className="p-4 rounded-4 glass-panel border-glow text-center d-flex flex-column align-items-center justify-content-center h-100">
            <Car size={42} className="text-cyan mb-3" />
            <h5 className="text-white fw-bold mb-2">Automotive Engineering</h5>
            <p className="text-secondary small mb-0 lh-base">
              Bridging hands-on automotive R&D design with contemporary academic research in strategic flexibility and technological innovation[cite: 2].
            </p>
          </div>
        </div>
      </div>

      {/* 2. Patent Feature Showcase */}
      <motion.div 
        initial={{ opacity: 0, y: 25 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="row g-4 align-items-stretch"
      >
        {/* Left: Patent Visual Preview */}
        <div className="col-12 col-lg-5">
          <div 
            className="p-3 rounded-4 glass-panel h-100 d-flex flex-column justify-content-between text-center position-relative overflow-hidden group"
            style={{ borderLeft: '4px solid #00f2fe' }}
          >
            <div className="position-relative overflow-hidden rounded-3 mb-3 bg-dark">
              <img 
                src={patentData.imageUrl} 
                alt={patentData.title}
                className="img-fluid rounded-3 w-100"
                style={{ maxHeight: '420px', objectFit: 'contain', cursor: 'zoom-in' }}
                onClick={() => setIsLightboxOpen(true)}
              />
              <button 
                type="button" 
                onClick={() => setIsLightboxOpen(true)}
                className="btn btn-sm btn-dark position-absolute bottom-0 end-0 m-3 px-3 py-1 rounded-pill d-inline-flex align-items-center gap-1 border border-secondary border-opacity-50"
                style={{ fontSize: '0.75rem' }}
              >
                <ZoomIn size={13} className="text-cyan" /> Click to Expand
              </button>
            </div>

            <div className="text-start px-2">
              <span className="badge px-3 py-1 rounded-pill small mb-2" style={{ background: 'rgba(0, 242, 254, 0.1)', color: '#00f2fe', border: '1px solid rgba(0, 242, 254, 0.3)' }}>
                {patentData.category}
              </span>
              <p className="text-secondary small mb-0">
                Official patent filing illustration for the Scorpio vehicle chassis platform.
              </p>
            </div>
          </div>
        </div>

        {/* Right: Technical Specifications & Impact */}
        <div className="col-12 col-lg-7">
          <div className="p-4 p-md-5 rounded-4 glass-panel h-100 d-flex flex-column justify-content-between">
            <div>
              {/* Meta Tags */}
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                <span className="badge bg-warning text-dark fw-bold px-3 py-1 rounded-pill small">
                  {patentData.status}
                </span>
                <span className="small text-warning fw-semibold d-inline-flex align-items-center gap-1">
                  <Calendar size={14} /> Filing: {patentData.applicationDate}
                </span>
              </div>

              {/* Patent Heading */}
              <h2 className="text-white fw-bold display-6 mb-3 fs-3">
                {patentData.title}
              </h2>

              <div className="mb-4 d-flex flex-column gap-2 text-secondary small">
                <div className="d-flex align-items-center gap-2">
                  <Building2 size={16} className="text-cyan flex-shrink-0" />
                  <span><strong>Assignee / Corporation:</strong> {patentData.assignee}</span>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <Award size={16} className="text-warning flex-shrink-0" />
                  <span><strong>Inventor:</strong> {patentData.inventor}</span>
                </div>
              </div>

              {/* Description */}
              <p className="text-light opacity-90 small lh-lg mb-4">
                {patentData.overview}
              </p>

              {/* Bullet Points */}
              <h6 className="text-white fw-bold mb-3 d-flex align-items-center gap-2">
                <Wrench size={16} className="text-cyan" /> Key Engineering Innovations:
              </h6>
              <div className="row g-2 mb-4">
                {patentData.highlights.map((point, i) => (
                  <div key={i} className="col-12">
                    <div className="p-2 rounded-3 bg-dark border border-secondary border-opacity-25 d-flex align-items-start gap-2">
                      <ShieldCheck size={16} className="text-success flex-shrink-0 mt-1" />
                      <span className="text-light small">{point}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Industrial Footer Note */}
            <div className="pt-3 border-top border-secondary border-opacity-25 d-flex justify-content-between align-items-center gap-2 text-secondary small">
              <span>Industrial Patent Portfolio</span>
              <span className="badge bg-secondary bg-opacity-25 text-info border border-secondary border-opacity-50">
                Automotive Division
              </span>
            </div>
          </div>
        </div>
      </motion.div>

      {/* 3. Lightbox Modal */}
      <AnimatePresence>
        {isLightboxOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsLightboxOpen(false)}
            className="position-fixed top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center p-3"
            style={{
              background: 'rgba(3, 7, 18, 0.94)',
              backdropFilter: 'blur(12px)',
              zIndex: 3000,
              cursor: 'zoom-out'
            }}
          >
            <motion.div
              initial={{ scale: 0.85 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.85 }}
              onClick={(e) => e.stopPropagation()}
              className="position-relative text-center"
              style={{ maxWidth: '90vw', maxHeight: '90vh' }}
            >
              <img 
                src={patentData.imageUrl} 
                alt={patentData.title}
                className="img-fluid rounded-4 shadow-lg border border-secondary border-opacity-50"
                style={{ maxHeight: '80vh', maxWidth: '85vw', objectFit: 'contain' }}
              />
              <div className="mt-3 text-center">
                <h6 className="text-white fw-bold mb-1">{patentData.title}</h6>
                <p className="text-secondary small mb-2">{patentData.assignee} ({patentData.applicationDate})</p>
                <button 
                  type="button" 
                  onClick={() => setIsLightboxOpen(false)}
                  className="btn btn-sm btn-outline-secondary rounded-pill px-4"
                >
                  Close Preview
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
}